/** Campaign objectives, in the order the design lists them, with plain-English help. */
export interface SparkObjectiveOption {
  /** TikTok `objective_type`. */
  apiValue: string;
  label: string;
  help: string;
}

export const SPARK_OBJECTIVES: readonly SparkObjectiveOption[] = [
  {
    apiValue: 'REACH',
    label: 'Reach',
    help: 'Show the post to as many people as possible, as cheaply as possible.',
  },
  {
    apiValue: 'VIDEO_VIEWS',
    label: 'Video views',
    help: 'Get the most watch time. Best for creator content that earns attention.',
  },
  {
    apiValue: 'TRAFFIC',
    label: 'Traffic',
    help: 'Send people to a page. Optimises for clicks, not what happens after.',
  },
  {
    apiValue: 'ENGAGEMENT',
    label: 'Community interaction',
    help: 'Grow follows and profile visits from the creator’s post.',
  },
  {
    apiValue: 'WEB_CONVERSIONS',
    label: 'Website conversions',
    help: 'Optimise for a purchase or sign-up on your site. Needs the TikTok Pixel.',
  },
  {
    apiValue: 'PRODUCT_SALES',
    label: 'Product sales',
    help: 'Drive checkouts from a connected catalogue or TikTok Shop.',
  },
  {
    apiValue: 'LEAD_GENERATION',
    label: 'Lead generation',
    help: 'Collect details through an instant form — no site needed.',
  },
] as const;

/** The objective the design pre-selects. */
export const DEFAULT_SPARK_OBJECTIVE = 'WEB_CONVERSIONS';

/** Age buckets, matching the service's own union. */
export type SparkAgeGroup =
  | 'AGE_18_24'
  | 'AGE_25_34'
  | 'AGE_35_44'
  | 'AGE_45_54'
  | 'AGE_55_100';

export const SPARK_AGE_GROUPS: readonly { value: SparkAgeGroup; label: string }[] = [
  { value: 'AGE_18_24', label: '18–24' },
  { value: 'AGE_25_34', label: '25–34' },
  { value: 'AGE_35_44', label: '35–44' },
  { value: 'AGE_45_54', label: '45–54' },
  { value: 'AGE_55_100', label: '55+' },
] as const;

export type SparkGender = 'GENDER_UNLIMITED' | 'GENDER_FEMALE' | 'GENDER_MALE';

export const SPARK_GENDERS: readonly { value: SparkGender; label: string }[] = [
  { value: 'GENDER_UNLIMITED', label: 'All' },
  { value: 'GENDER_FEMALE', label: 'Female' },
  { value: 'GENDER_MALE', label: 'Male' },
] as const;

/** Calls to action offered on a Spark ad. */
export const SPARK_CALLS_TO_ACTION: readonly { value: string; label: string }[] = [
  { value: 'SHOP_NOW', label: 'Shop now' },
  { value: 'LEARN_MORE', label: 'Learn more' },
  { value: 'SIGN_UP', label: 'Sign up' },
  { value: 'WATCH_NOW', label: 'Watch more' },
  { value: 'GET_QUOTE', label: 'Get quote' },
] as const;

/**
 * TikTok's minimum daily ad-group budget.
 *
 * Enforced client-side so the user is told before submitting rather than seeing a
 * rejection come back from TikTok.
 */
export const MIN_DAILY_BUDGET = 20;

export const MIN_LIFETIME_BUDGET = 50;

export const BUDGET_SLIDER_MAX = 2000;

export const BUDGET_SLIDER_STEP = 10;

export const STEP_DEFINITIONS: readonly {
  step: 1 | 2 | 3 | 4;
  label: string;
  sub: string;
}[] = [
  { step: 1, label: 'Campaign', sub: 'Goal and spending cap' },
  { step: 2, label: 'Ad group', sub: 'Audience, budget, schedule' },
  { step: 3, label: 'Spark ad', sub: 'Creator post and CTA' },
  { step: 4, label: 'Review & launch', sub: 'Check everything, then go' },
] as const;
